import { useEffect, useRef, useState } from 'react';
import { walkEventsUrl } from '@/api/config';
import { Stamp } from '@/components/ui';
import { ToolIcon } from '@/components/ToolIcon';
import { say } from '@/lib/vocabulary';
import { walkWait } from './walkWait';

/** Marks the row can show without running into the counts beside it. */
const ICONS_SHOWN = 6;

export interface Frame {
  bucketId: string;
  state: string;
  populationCount: number;
  sealedCount: number;
  toolDispositions: number;
  strayDispositions: number;
  tools: string[];
  failureCode: string | null;
  percentComplete: number;
}

export type WalkState = 'connecting' | 'live' | 'sealed' | 'failed' | 'none' | 'error';

/**
 * Live attribution-walk progress over SSE (the backend's blessed EventSource
 * contract: complete snapshots, no cursors, terminal events close the
 * stream). percentComplete is exact, never an estimate; the tools ticker
 * grows as the walk seals. Fixed-height layout: state changes never reflow.
 */
export function WalkProgress({
  bucketId,
  onSealed,
  onFrame,
  onState,
  compact = false,
}: {
  bucketId: string;
  /** Receives the terminal snapshot, so callers can gate on the live seal
   *  before the sealed-toolset projection catches up. */
  onSealed?: (frame: Frame) => void;
  /** Receives every snapshot. The walk names each tool the moment it seals
   *  its first object, which is how a view can grow in real time instead of
   *  waiting for the whole run to finish. */
  onFrame?: (frame: Frame) => void;
  /** Reports the stream's own verdict. A caller must not infer "no walk"
   *  from an absent or failed read elsewhere; only the stream knows. */
  onState?: (state: WalkState) => void;
  /** Drops the trailing explanation for embedding inside a denser surface. */
  compact?: boolean;
}) {
  const [frame, setFrame] = useState<Frame | null>(null);
  const [state, setState] = useState<WalkState>('connecting');
  const sealedNotified = useRef(false);
  /**
   * When this strip started watching, and when it last heard anything.
   *
   * Without these the strip had no way to distinguish a walk that is about to
   * report from one that never will, and said the same sentence about both.
   */
  const [since, setSince] = useState(() => Date.now());
  const [lastFrameAt, setLastFrameAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // A second hand for the wait. It only has to be accurate to the second, and
  // it stops once the run is terminal so a sealed strip is not repainting.
  useEffect(() => {
    if (state === 'sealed' || state === 'failed' || state === 'none') return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [state]);

  useEffect(() => {
    setSince(Date.now());
    setLastFrameAt(null);
    setFrame(null);
    setState('connecting');
    sealedNotified.current = false;
    const source = new EventSource(walkEventsUrl(bucketId), { withCredentials: true });
    let terminal = false;
    const render = (e: MessageEvent) => {
      const f = JSON.parse(e.data as string) as Frame;
      setFrame(f);
      setLastFrameAt(Date.now());
      onFrame?.(f);
    };
    source.addEventListener('walk-progress', (e) => {
      setState('live');
      render(e as MessageEvent);
    });
    source.addEventListener('walk-sealed', (e) => {
      terminal = true;
      setState('sealed');
      render(e as MessageEvent);
      source.close();
      if (!sealedNotified.current) {
        sealedNotified.current = true;
        onSealed?.(JSON.parse((e as MessageEvent).data as string) as Frame);
      }
    });
    source.addEventListener('walk-failed', (e) => {
      terminal = true;
      setState('failed');
      render(e as MessageEvent);
      source.close();
    });
    source.addEventListener('no-walk', () => {
      terminal = true;
      setState('none');
      source.close();
    });
    // The walk emits internal-error as well as dependency-error. Hearing only
    // one left a failed walk stuck reporting "connecting" indefinitely.
    for (const failure of ['dependency-error', 'internal-error']) {
      source.addEventListener(failure, () => {
        terminal = true;
        setState('error');
        source.close();
      });
    }
    source.onerror = () => {
      // After a terminal event this is the normal close; otherwise
      // EventSource auto-reconnects (frames are complete snapshots).
      if (!terminal) setState((s) => (s === 'connecting' ? 'connecting' : s));
    };
    return () => source.close();
  }, [bucketId, onSealed, onFrame]);

  useEffect(() => {
    onState?.(state);
  }, [state, onState]);

  // A percentage only exists once a frame carries one. Defaulting to zero
  // draws a confident empty bar while the stream is still connecting, which
  // reads as a walk that has done nothing rather than one not yet heard from.
  /**
   * A percentage of nothing is not a percentage.
   *
   * A walk that has opened its population but not counted it reports
   * percentComplete 100 over populationCount 0, and the strip drew a full gold
   * bar above "0 / 0 objects sealed". Complete and empty at the same time, on
   * the one element an operator reads to decide whether to keep waiting.
   */
  const counted = (frame?.populationCount ?? 0) > 0;
  const pct = frame && counted ? frame.percentComplete : null;
  const tone = state === 'sealed' ? 'ok' : state === 'failed' || state === 'error' ? 'bad' : 'mut';
  const label =
    state === 'connecting'
      ? 'connecting'
      : state === 'live'
        ? say(frame?.state ?? 'WALKING')
        : state === 'sealed'
          ? 'sealed'
          : state === 'failed'
            ? say(frame?.failureCode ?? 'FAILED')
            : state === 'none'
              ? 'no walk yet'
              : 'stream lost';
  // A transport fault states a problem and no next move unless it says whether
  // the work itself is still running.
  const transportNote =
    state === 'error'
      ? 'The live progress feed dropped. The walk itself keeps running; reload to pick it up again.'
      : null;
  /**
   * The wait, measured.
   *
   * This is what the strip was missing: it promised "a second or two" at two
   * seconds and at twenty minutes, because nothing ever arrived to correct the
   * sentence, and on a walk that never reports nothing ever will.
   */
  const wait = walkWait(state, since, lastFrameAt, now);

  return (
    <div className="flex-none border-b border-line bg-panel">
    <div className="flex h-16 items-center gap-4 px-4">
      <Stamp tone={tone} className="w-44 justify-center whitespace-nowrap">{label}</Stamp>
      <div className="min-w-0 flex-1">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-panel-2">
          <div
            className="h-full rounded-full bg-acc transition-[width] duration-500"
            style={{ width: `${pct ?? 0}%` }}
          />
        </div>
        {transportNote && <p className="mb-1 text-small text-warn">{transportNote}</p>}
        {!transportNote && wait.overdue && wait.note && (
          <p role="status" className="mb-1 text-small text-warn">{wait.note}</p>
        )}
        <div className="mono mt-1 flex flex-wrap gap-x-4 text-caption text-ink-3">
          <span className="inline-block min-w-[52px]">{pct === null ? '—' : `${pct}%`}</span>
          {frame && !counted && (
            <span className="whitespace-nowrap text-ink-4">no population counted yet</span>
          )}
          <span className="whitespace-nowrap">
            {frame
              ? `${frame.sealedCount.toLocaleString()} / ${frame.populationCount.toLocaleString()} objects sealed`
              : state === 'none'
                ? 'this scope has never been walked'
                : (wait.overdue ? `no walk has reported in ${wait.elapsed}` : (wait.note ?? ''))}
          </span>
          {/* How long this has been going. A screen that shows progress and
              hides the clock cannot be used to judge whether to keep waiting. */}
          {(state === 'live' || state === 'connecting') && (
            <span className={`whitespace-nowrap tabular-nums ${wait.overdue ? 'text-warn' : 'text-ink-4'}`}>
              {wait.elapsed}
            </span>
          )}
          {frame && (
            <span className="whitespace-nowrap">
              {/* "tool · stray" is the ledger's own two-word vocabulary. What
                  it means to a person is how many files have been traced to
                  the software that made them, and how many have not. */}
              {frame.toolDispositions.toLocaleString()} traced ·{' '}
              {frame.strayDispositions.toLocaleString()} not traced
            </span>
          )}
        </div>
      </div>
      {/* capway seals sixteen tools, and eight icons at this size already
          overflowed a fixed 176px box and drew straight over the counts to
          their left. The row is bounded and says how many it is not showing. */}
      <div className="flex w-44 flex-none items-center justify-end gap-1.5 overflow-hidden">
        {(frame?.tools ?? []).slice(0, ICONS_SHOWN).map((slug) => (
          <span key={slug} className="animate-pop flex-none" title={slug}>
            <ToolIcon tool={slug} size={18} />
          </span>
        ))}
        {(frame?.tools.length ?? 0) > ICONS_SHOWN && (
          <span className="mono flex-none text-caption text-ink-3">+{(frame!.tools.length - ICONS_SHOWN).toLocaleString()}</span>
        )}
        {(frame?.tools.length ?? 0) === 0 && <span className="text-caption text-ink-4">tools appear as the walk seals</span>}
      </div>
    </div>
    {state === 'sealed' && !compact && (
      /* This sentence named the machinery: evidence grading, 0/10 signals,
         entity publication, relationship lenses, and it invited a click on a
         tool where nothing on the strip is clickable. */
      <p className="border-t border-line px-4 py-1.5 text-caption text-ink-3">
        The walk is finished and its result is fixed. What happens next needs no one here: each candidate tool is graded
        against the evidence found for it, and confirmed tools are what let a package place files.
      </p>
    )}
    </div>
  );
}
